import { useAppSelector } from 'hooks/redux';
import React from 'react';
import { useDispatch } from 'react-redux';

// DATA
import { aggErrors } from 'app-components/flux/account-discovery';
import { accountsUtils } from 'app-components/flux/accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransaction,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import type { transactionsTypes } from 'app-components/flux/transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import { getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import { doBillPresentment } from '../BillPresentment/actions';

const log = getLogger('BillStatus');

import DownloadingIcon from '@mui/icons-material/DownloadingRounded';
import ErrorIcon from '@mui/icons-material/ErrorOutlineRounded';
import HourglassIcon from '@mui/icons-material/HourglassBottomRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import UpdatedIcon from '@mui/icons-material/VerifiedRounded';
import type { PaletteColor } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

// CUSTOM
import QButton from 'components/QButton';

const useStyles = makeStyles<{ statusColor: PaletteColor }>()((_theme, { statusColor }) => ({
  billStatusRoot: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    borderRadius: 12,
    marginTop: 8,
    backgroundColor: statusColor.light,
  },
  icon: {
    height: 24,
    width: 24,
    marginRight: 8,
    color: statusColor.main,
  },
  button: {
    flexShrink: 0,
    marginLeft: 4,
  },
}));

interface BillStatusProps {
  txn?: transactionsTypes.TransactionProps;
  model?: ScheduledTransaction;
  onClose: () => void;
}

function BillStatus(props: BillStatusProps) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { txn, model, onClose } = props;

  const creditAccount = useAppSelector((state) => txn && transactionsSelectors.getCreditAccountFromTxn(state, txn));
  const institutionLogin = useAppSelector((state) =>
    creditAccount?.institutionLoginId
      ? institutionLoginsSelectors.getInstitutionLoginForId(state, creditAccount.institutionLoginId)
      : undefined,
  );

  const { autoAdjustAmount, autoAdjustDueOn } = model || {};

  const hasBVA = Boolean(
    creditAccount &&
      transactionsUtils.isTransferTxn(txn) &&
      ((autoAdjustAmount && autoAdjustAmount !== AutoAdjustAmountEnum.none) || autoAdjustDueOn),
  );

  let statusColor = theme.palette.info;
  let Icon = LinkIcon;
  let text = 'The amount and date of this reminder will be updated once your statement period begins.';
  let showReconnect = false;
  let reconnectHandler: (() => void) | null = null;

  if (hasBVA) {
    if (accountsUtils.isAccountInError(creditAccount)?.isError) {
      // BVA error
      statusColor = theme.palette.error;
      Icon = ErrorIcon;
      text = 'Linked account has been disconnected \nand reminder details can’t be updated.';
      showReconnect = true;
      reconnectHandler = () => {
        const aggregation = creditAccount?.aggregators?.get(0);
        const aggAction = aggErrors.getQcsAggregationAction(
          institutionLogin,
          aggregation?.aggStatus,
          aggregation?.aggStatusCode,
          store && featureFlagsSelectors.getFeatureFlags(store.getState()),
        );
        if (aggAction) {
          aggErrors.handleAggAction(dispatch, aggAction, 'BVA connection status');
        } else {
          log.error(
            'No aggAction available for BillStatus reconnect',
            '\nCredit Account ID:',
            creditAccount?.id,
            '\nInstitution Login ID:',
            institutionLogin?.id,
            '\nInstitution:',
            institutionLogin?.name,
            '\nAgg Status:',
            aggregation?.aggStatus,
            '\nAgg Status Code:',
            aggregation?.aggStatusCode,
          );
        }
        onClose();
      };
    } else {
      // BVA displays
      text = 'The amount and date of this reminder will be updated once your statement period begins.';
      if (txn?.ebillId === '3') {
        Icon = DownloadingIcon;
        text =
          'The statement period for this bill has begun, and the reminder amount will continue to update automatically.';
      } else if (txn && (txn.ebillId === '1' || txn.ebillId === '2')) {
        statusColor = theme.palette.success;
        Icon = UpdatedIcon;
        text = 'The amount and date of this reminder has been updated with your finalized bill information.';
      }
    }
  } else if (model?.billerEnrollmentStatus === 'FAILED') {
    // FiServ Error
    statusColor = theme.palette.error;
    Icon = ErrorIcon;
    text = 'Biller needs attention. Reconnect now to keep viewing your bills.';
    showReconnect = true;
    reconnectHandler = () => {
      dispatch(doBillPresentment(model, 'bill_status_reconnect'));
      onClose();
    };
  } else if (model?.billerEnrollmentStatus === 'PENDING') {
    Icon = HourglassIcon;
    text = 'Connecting to your biller. This may take up to a day or two.';
  } else if (model?.billerEnrollmentStatus === 'CONNECTED') {
    if (txn?.ebillId) {
      statusColor = theme.palette.success;
      Icon = UpdatedIcon;
      text = 'The amount and date of this reminder have been updated with your latest bill information.';
    } else {
      text = 'The amount and date of this reminder will be updated once your biller issues a new bill.';
    }
  }

  const { classes } = useStyles({ statusColor });

  if (!hasBVA && !model?.presentmentAccountId) {
    return null;
  }

  return (
    <div className={classes.billStatusRoot}>
      <Icon className={classes.icon} />

      <Typography variant='captionRegular'>{text}</Typography>

      {showReconnect && reconnectHandler && (
        <QButton className={classes.button} variant='edit' id='bill-reconnect' onClick={reconnectHandler}>
          Reconnect
        </QButton>
      )}
    </div>
  );
}

BillStatus.displayName = 'BillStatus';

export default BillStatus;
